import { useCallback } from 'react';
import { useFeedbackContext } from './FeedbackContext';
import type { FeedbackFormData, FeedbackPayload, FeedbackResponse } from './types';

interface UseFeedbackReturn {
  submit: (data: FeedbackFormData) => Promise<FeedbackResponse>;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
  successMessage: string | null;
  reset: () => void;
}

export function useFeedback(): UseFeedbackReturn {
  const {
    config,
    getContext,
    submissionState,
    setSubmissionState,
    errorMessage,
    setErrorMessage,
    successMessage,
    setSuccessMessage,
    closeForm,
    screenshot,
  } = useFeedbackContext();

  const submit = useCallback(
    async (data: FeedbackFormData): Promise<FeedbackResponse> => {
      setSubmissionState('loading');
      setErrorMessage(null);

      try {
        const context = await getContext();

        const payload: FeedbackPayload = {
          title: data.title,
          description: data.description,
          context,
          projectKey: config.projectKey,
          issueType: config.issueType,
          ...(data.includeScreenshot && screenshot ? { screenshot } : {}),
        };

        let response = await fetch(config.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        // If payload too large (413), retry without screenshot
        if (response.status === 413 && payload.screenshot) {
          console.warn('[FeedbackWidget] Payload too large, retrying without screenshot...');
          const payloadWithoutScreenshot = { ...payload };
          delete payloadWithoutScreenshot.screenshot;

          response = await fetch(config.apiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payloadWithoutScreenshot),
          });
        }

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }

        const result: FeedbackResponse = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.error || 'Failed to submit feedback');
        }

        setSubmissionState('success');
        setSuccessMessage(`Feedback submitted successfully! Issue: ${result.issueKey}`);

        setTimeout(() => {
          closeForm();
        }, 2000);

        return result;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'An unexpected error occurred';
        setSubmissionState('error');
        setErrorMessage(message);
        return { success: false, error: message };
      }
    },
    [config, getContext, setSubmissionState, setErrorMessage, setSuccessMessage, closeForm, screenshot]
  );

  const reset = useCallback(() => {
    setSubmissionState('idle');
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [setSubmissionState, setErrorMessage, setSuccessMessage]);

  return {
    submit,
    isLoading: submissionState === 'loading',
    isSuccess: submissionState === 'success',
    isError: submissionState === 'error',
    errorMessage,
    successMessage,
    reset,
  };
}
